import { Injectable } from '@angular/core';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';

@Injectable({
    providedIn: 'root',
})
export class ComponentService {
    constructor(private datatask: DataTaskService) {}

    /**
     * Retrieves one product component.
     *
     * @datatask 707
     * @datataskName sp_ws_p2_component_get
     * @param params product/component identifiers.
     * @returns promise containing component get data.
     */
    public async getComponent(params: GetComponentParams): Promise<ComponentGet> {
        return (await this.datatask.Post(707, params))[0];
    }

    /**
     * Retrieves component values when the selected product changes.
     *
     * @datatask 708
     * @datataskName sp_ws_p2_w_component_prod_changed
     * @param params selected component product and parent product.
     * @returns promise containing changed component values.
     */
    public async updateComponentProduct(params: UpdateComponentProductParams): Promise<UpdateComponentProductResponse> {
        return (await this.datatask.Post(708, params))[0];
    }

    /**
     * Recalculates component quantities.
     *
     * @datatask 2948
     * @datataskName sp_ws_p2_w_component_helpcalc
     * @param params current component get data and changed column.
     * @returns promise containing recalculated values.
     */
    public async calculateComponent(params: CalculateComponentParams): Promise<CalculateComponentResponse> {
        return (await this.datatask.Post(2948, params))[0];
    }

    /**
     * Saves a product component.
     *
     * @datatask 719
     * @datataskName sp_ws_p2_component_save
     * @param params component get data.
     * @returns promise containing validation response.
     */
    public async saveComponent(params: ComponentGet): Promise<SaveComponentResponse> {
        return (await this.datatask.Post(719, params))[0];
    }

    /**
     * Deletes a product component.
     *
     * @datatask 720
     * @datataskName sp_ws_p2_component_delete
     * @param params component get data.
     * @returns promise containing validation response when returned by the backend.
     */
    public async deleteComponent(params: ComponentGet): Promise<DeleteComponentResponse> {
        return (await this.datatask.Post(720, params))[0];
    }
}

export interface GetComponentParams {
    prod_id: string;
    comp_keyno: string;
}

export type ComponentCalculationField = 'special_length' | 'special_width' | 'special_quantity' | 'special_quantity_formula' | 'input_unit_no' | 'input_quantity' | 'input_calc_quantity';

export interface CalculateComponentParams extends ComponentGet {
    changedcol: ComponentCalculationField;
}

export interface UpdateComponentProductParams {
    comp_is_prod_id: string;
    prod_id: string;
}

export interface UpdateComponentProductResponse {
    comp_is_prod_id: string;
    comp_prod_name: string;
    prodsize_keyno: string;
    prodchoice_keyno: string;
    unit_name_stock: string;
    nbrof_prodsize: string;
    nbrof_prodchoice: string;
    stock_unit_no: string;
    prod_text3: string;
    waste_pst: string;
    url_picture: string;
}

export interface CalculateComponentResponse extends ErrorMessageResponse {
    quantity: string | number;
    input_quantity: string | number;
}

export interface ComponentGet {
    page_heading: string;
    prod_id: string;
    prod_keyno: string;
    product_search: string;
    comp_keyno: string;
    comp_is_prod_id: string;
    comp_prod_name: string;
    quantity: string | number;
    comp_lineno: string | number;
    stock_unit_no: string;
    comp_waste_pst: string | number;
    choicevalidation: '1' | '0';
    note: string;
    show_replaceable_comp_choice_prod_id_2: '1' | '0';
    replaceable_comp_choice_prod_id_2: string;
    show_special_fields: '1' | '0';
    sublabel_helpcalc: string;
    special_length: string | number;
    special_width: string | number;
    special_quantity: string | number;
    special_quantity_formula: string;
    input_unit_no: string;
    input_quantity: string | number;
    input_calc_quantity: string | number;
    url_picture: string;
    prod_id_new: string;
    show_setting: '1' | '0';
    nbr_of_compvalidation: string;
}

export interface SaveComponentResponse extends ErrorMessageResponse {
    comp_keyno: `${number}`;
}

export interface DeleteComponentResponse {
    status_no: number | `${number}`;
    status_message: string;
}
